import { For, Show, Suspense, createSignal } from "solid-js";
import { useParams, A, createAsync, useAction, type RouteDefinition } from "@solidjs/router";
import { Title } from "@solidjs/meta";
import { AppShell } from "~/components/layout";
import {
  Button, Badge, Card, CardContent, Skeleton, EmptyState, Avatar,
  Tabs, TabsList, TabsTrigger, TabsContent, Breadcrumbs
} from "~/components/ui";
import { getChannel, getVideos, getPredictions, toggleChannelMonitoring } from "~/server/bff";
import { formatRelativeTime, formatDuration, formatDate } from "~/lib/utils";
import {
  Edit, Pause, Play, Video, Target, Tag, BarChart3, ArrowLeft, ExternalLink
} from "lucide-solid";

export const route = {
  preload: ({ params }) =>
    Promise.all([
      getChannel(params.id as string),
      getVideos({ channelId: params.id as string }),
      getPredictions({ channelId: params.id as string }),
    ]),
} satisfies RouteDefinition;

export default function ChannelDetailPage() {
  const params = useParams();
  const [activeTab, setActiveTab] = createSignal("overview");
  const [isTogglingMonitoring, setIsTogglingMonitoring] = createSignal(false);

  const toggleMonitoringAction = useAction(toggleChannelMonitoring);
  const channel = createAsync(() => getChannel(params.id || ""), { deferStream: true });
  const videos = createAsync(() => getVideos({ channelId: params.id }), { deferStream: true });
  const predictions = createAsync(() => getPredictions({ channelId: params.id }), { deferStream: true });

  const handleToggleMonitoring = async (channelId: string, isMonitored: boolean) => {
    setIsTogglingMonitoring(true);
    try {
      const formData = new FormData();
      formData.append("channelId", channelId);
      formData.append("enabled", (!isMonitored).toString());
      await toggleMonitoringAction(formData);
    } finally {
      setIsTogglingMonitoring(false);
    }
  };

  return (
    <AppShell>
      <Title>{channel()?.name ?? "Source"} - IntelAnalyst</Title>
      <div class="p-6">
        <Suspense fallback={<ChannelHeaderSkeleton />}>
          <Show when={channel()}>
            {(c) => (
              <>
                {/* Breadcrumbs */}
                <Breadcrumbs
                  items={[
                    { label: "Explore", href: "/explore/search" },
                    { label: "Sources", href: "/explore/sources" },
                    { label: c().name },
                  ]}
                  showHome
                  class="mb-4"
                />

                <div class="flex items-start gap-6 mb-6">
                  <Avatar src={c().thumbnailUrl} alt={c().name} size="lg" class="h-20 w-20" />
                  <div class="flex-1">
                    <div class="flex items-center gap-2">
                      <h1 class="text-2xl font-bold">{c().name}</h1>
                      <a
                        href={`https://youtube.com/${c().handle}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-muted-foreground hover:text-foreground"
                        aria-label="Open channel on YouTube"
                        title="Open channel on YouTube"
                      >
                        <ExternalLink class="h-4 w-4" />
                      </a>
                    </div>
                    <p class="text-muted-foreground">{c().handle}</p>
                    <Show when={c().description}>
                      <p class="text-sm mt-2">{c().description}</p>
                    </Show>
                    <div class="flex flex-wrap gap-1 mt-3">
                      <For each={c().tags}>
                        {(tag) => <Badge variant="outline">{tag}</Badge>}
                      </For>
                    </div>
                  </div>
                  <div class="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isTogglingMonitoring()}
                      onClick={() => void handleToggleMonitoring(c().id, c().isMonitored)}
                    >
                      <Show when={c().isMonitored} fallback={<Play class="h-4 w-4 mr-1" />}>
                        <Pause class="h-4 w-4 mr-1" />
                      </Show>
                      {isTogglingMonitoring() ? "..." : c().isMonitored ? "Pause" : "Resume"}
                    </Button>
                    <A href={`/explore/sources/${c().id}/edit`}>
                      <Button variant="outline" size="sm">
                        <Edit class="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </A>
                  </div>
                </div>

                <div class="grid grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardContent class="p-4">
                      <p class="text-2xl font-bold">{c().videoCount}</p>
                      <p class="text-sm text-muted-foreground">Videos</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent class="p-4">
                      <p class="text-2xl font-bold">{c().predictionCount}</p>
                      <p class="text-sm text-muted-foreground">Predictions</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent class="p-4">
                      <p class="text-2xl font-bold">{c().accuracy ?? "N/A"}%</p>
                      <p class="text-sm text-muted-foreground">Accuracy</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent class="p-4">
                      <p class="text-2xl font-bold">
                        {c().lastVideoDate ? formatRelativeTime(c().lastVideoDate!) : "N/A"}
                      </p>
                      <p class="text-sm text-muted-foreground">Last Video</p>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </Show>
        </Suspense>

        <Tabs value={activeTab()} onChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="videos">
              <Video class="h-4 w-4 mr-1" />
              Videos
            </TabsTrigger>
            <TabsTrigger value="predictions">
              <Target class="h-4 w-4 mr-1" />
              Predictions
            </TabsTrigger>
            <TabsTrigger value="topics">
              <Tag class="h-4 w-4 mr-1" />
              Topics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" class="mt-4">
            <div class="grid gap-6 md:grid-cols-2">
              <Card>
                <CardContent class="p-4">
                  <h3 class="font-medium mb-3">Recent Videos</h3>
                  <Suspense fallback={<Skeleton class="h-32" />}>
                    <Show when={videos()} fallback={<Skeleton class="h-32" />}>
                      {(vids) => (
                        <div class="space-y-2">
                          <For each={vids().slice(0, 3)}>
                            {(video) => (
                              <A href={`/video/${video.id}`} class="flex gap-3 p-2 rounded hover:bg-accent">
                                <img src={video.thumbnailUrl} alt="" class="h-12 w-20 rounded object-cover" />
                                <div class="flex-1 min-w-0">
                                  <p class="text-sm font-medium line-clamp-1">{video.title}</p>
                                  <p class="text-xs text-muted-foreground">{formatRelativeTime(video.publishedAt)}</p>
                                </div>
                              </A>
                            )}
                          </For>
                        </div>
                      )}
                    </Show>
                  </Suspense>
                </CardContent>
              </Card>

              <Card>
                <CardContent class="p-4">
                  <h3 class="font-medium mb-3">Recent Predictions</h3>
                  <Suspense fallback={<Skeleton class="h-32" />}>
                    <Show when={predictions()} fallback={<Skeleton class="h-32" />}>
                      {(preds) => (
                        <Show
                          when={preds().length > 0}
                          fallback={<p class="text-sm text-muted-foreground">No predictions yet</p>}
                        >
                          <div class="space-y-2">
                            <For each={preds().slice(0, 3)}>
                              {(prediction) => (
                                <A href={`/track/predictions/${prediction.id}`} class="block p-2 rounded hover:bg-accent">
                                  <p class="text-sm font-medium line-clamp-1">{prediction.event}</p>
                                  <div class="flex items-center gap-2 mt-1">
                                    <Badge variant="outline" class="text-xs">{prediction.status}</Badge>
                                    <Show when={prediction.targetDate}>
                                      <span class="text-xs text-muted-foreground">
                                        Due: {formatDate(prediction.targetDate!)}
                                      </span>
                                    </Show>
                                  </div>
                                </A>
                              )}
                            </For>
                          </div>
                        </Show>
                      )}
                    </Show>
                  </Suspense>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="videos" class="mt-4">
            <Suspense fallback={<Skeleton class="h-64" />}>
              <Show when={videos()} fallback={<Skeleton class="h-64" />}>
                {(items) => (
                  <Show
                    when={items().length > 0}
                    fallback={
                      <EmptyState
                        icon={<Video class="h-12 w-12" />}
                        title="No videos"
                        description="Videos from this channel will appear here"
                      />
                    }
                  >
                    <div class="space-y-3">
                      <For each={items()}>
                        {(video) => (
                          <A href={`/video/${video.id}`}>
                            <Card class="hover:bg-accent/50 transition-colors">
                              <CardContent class="p-4">
                                <div class="flex gap-4">
                                  <div class="relative flex-shrink-0">
                                    <img src={video.thumbnailUrl} alt="" class="h-20 w-36 rounded object-cover" />
                                    <div class="absolute bottom-1 right-1 rounded bg-black/80 px-1 py-0.5 text-xs text-white">
                                      {formatDuration(video.duration)}
                                    </div>
                                  </div>
                                  <div class="flex-1 min-w-0">
                                    <h3 class="font-medium line-clamp-2">{video.title}</h3>
                                    <p class="text-sm text-muted-foreground mt-1">
                                      {formatRelativeTime(video.publishedAt)}
                                    </p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </A>
                        )}
                      </For>
                    </div>
                  </Show>
                )}
              </Show>
            </Suspense>
          </TabsContent>

          <TabsContent value="predictions" class="mt-4">
            <Suspense fallback={<Skeleton class="h-64" />}>
              <Show when={predictions()} fallback={<Skeleton class="h-64" />}>
                {(items) => (
                  <Show
                    when={items().length > 0}
                    fallback={
                      <EmptyState
                        icon={<Target class="h-12 w-12" />}
                        title="No predictions"
                        description="Predictions from this creator will appear here"
                      />
                    }
                  >
                    <div class="space-y-3">
                      <For each={items()}>
                        {(prediction) => (
                          <A href={`/track/predictions/${prediction.id}`}>
                            <Card class="hover:bg-accent/50 transition-colors">
                              <CardContent class="p-4">
                                <div class="flex items-start justify-between">
                                  <div>
                                    <p class="font-medium">{prediction.event}</p>
                                    <p class="text-sm text-muted-foreground italic mt-1">
                                      "{prediction.originalQuote}"
                                    </p>
                                  </div>
                                  <Badge
                                    variant={
                                      prediction.status === "verified" ? "success" :
                                      prediction.status === "failed" ? "destructive" : "outline"
                                    }
                                  >
                                    {prediction.status}
                                  </Badge>
                                </div>
                              </CardContent>
                            </Card>
                          </A>
                        )}
                      </For>
                    </div>
                  </Show>
                )}
              </Show>
            </Suspense>
          </TabsContent>

          <TabsContent value="topics" class="mt-4">
            <Card>
              <CardContent class="p-8 text-center">
                <Tag class="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <div class="flex items-center justify-center gap-2 mb-2">
                  <h3 class="font-medium">Topic Coverage</h3>
                  <Badge variant="secondary">Coming Soon</Badge>
                </div>
                <p class="text-sm text-muted-foreground">
                  Analyze which topics this creator covers most frequently
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

function ChannelHeaderSkeleton() {
  return (
    <div class="space-y-4">
      <div class="flex items-start gap-6">
        <Skeleton class="h-20 w-20 rounded-full" />
        <div class="flex-1 space-y-2">
          <Skeleton class="h-8 w-48" />
          <Skeleton class="h-4 w-32" />
          <Skeleton class="h-4 w-full max-w-md" />
        </div>
      </div>
      <div class="grid grid-cols-4 gap-4">
        <Skeleton class="h-20" />
        <Skeleton class="h-20" />
        <Skeleton class="h-20" />
        <Skeleton class="h-20" />
      </div>
    </div>
  );
}
